import { AlertTriangle, CheckCircle2, ChevronDown, Layers } from 'lucide-react';
import React, { useState } from 'react';

import type { LiqMapSnapshot } from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import type { PositionType } from '../trading/types';

import { checkTrade, formatNotional } from './clusters';
import {
    explainCheck,
    explainCluster,
    explainReach,
    explainSource,
    formatLevelPrice,
} from './copy';

const CHIP_LABEL = {
    ok: 'Stop is clear',
    warn: 'Stop is close',
    danger: 'Stop in a cluster',
} as const;

interface LiqMapLegendProps {
    coin: string;
    snapshot: LiqMapSnapshot | null;
    /** the trader's own open position on this coin, if any */
    trade: {
        type: PositionType;
        stopLossPrice: number | null;
        liquidationPrice: number | null;
    } | null;
    className?: string;
}

/**
 * The card that makes the chart's bands mean something.
 *
 * Collapsed it is one chip: the label and, when there is a position, the
 * verdict. The on/off switch is NOT here — it sits in the chart's own toolbar
 * (TradingView's header in candles mode, the chart header strip in live mode),
 * and this card only renders while the bands are on. Opened it names the two
 * biggest clusters on
 * each side in plain words, checks the trader's own stop and liquidation
 * against them, and says what the sample actually is — so the number is
 * trusted for the right reason and not because it is on a chart.
 */
// Memoised: it lives inside the chart panel, whose parent re-renders on every
// price tick, and nothing in it changes per tick — the snapshot is a stable
// query result and the trade levels are memoised upstream.
export const LiqMapLegend: React.FC<LiqMapLegendProps> = React.memo(
    function LiqMapLegend({
        coin,
        snapshot,
        trade,
        className,
    }: LiqMapLegendProps) {
        const [open, setOpen] = useState(false);
        const check = trade ? checkTrade(trade, snapshot) : null;
        const verdict = check && trade ? explainCheck(check, trade.type) : null;
        const hasMap =
            !!snapshot &&
            (snapshot.longs.length > 0 || snapshot.shorts.length > 0);

        return (
            <div
                className={cn(
                    'pointer-events-auto flex max-w-[min(360px,calc(100%-16px))] flex-col rounded-lg border border-border/70 bg-card/90 text-[11px] shadow-lg backdrop-blur-sm',
                    className,
                )}
                data-slot="liq-map-legend"
            >
                {/* chip row */}
                <div className="flex items-center gap-1.5 px-2 py-1.5">
                    <span className="flex items-center gap-1 px-1.5 py-0.5 font-semibold uppercase tracking-[0.08em] text-primary">
                        <Layers size={11} />
                        Liq zones
                    </span>
                    {verdict && hasMap && (
                        <span
                            className={cn(
                                'flex items-center gap-1 font-semibold',
                                verdict.tone === 'danger' && 'text-destructive',
                                verdict.tone === 'warn' && 'text-amber-300',
                                verdict.tone === 'ok' && 'text-primary',
                            )}
                            title={verdict.text}
                        >
                            {verdict.tone === 'ok' ? (
                                <CheckCircle2 size={11} />
                            ) : (
                                <AlertTriangle size={11} />
                            )}
                            {CHIP_LABEL[verdict.tone]}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        aria-expanded={open}
                        aria-label={
                            open
                                ? 'Hide explanation'
                                : 'What do the zones mean?'
                        }
                        className="ml-auto flex cursor-pointer items-center rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <ChevronDown
                            size={12}
                            className={cn(
                                'transition-transform',
                                open && 'rotate-180',
                            )}
                        />
                    </button>
                </div>

                {open && (
                    <div className="flex flex-col gap-2 border-t border-border/50 px-2.5 py-2 leading-snug">
                        {!snapshot || !hasMap ? (
                            <p className="text-muted-foreground">
                                {snapshot === null
                                    ? `No liquidation map for ${coin} yet — the sampler has not seen a position here.`
                                    : 'Loading the map…'}
                            </p>
                        ) : (
                            <>
                                <p className="text-foreground/90">
                                    Bands show where other traders' positions
                                    are{' '}
                                    <span className="font-semibold">
                                        force-closed
                                    </span>{' '}
                                    if price gets there. Big bands act like
                                    magnets, and like accelerators once hit.
                                </p>
                                <p className="text-muted-foreground">
                                    {explainReach(coin, snapshot)}
                                </p>

                                <ClusterList
                                    title="Below — longs get liquidated"
                                    tone="long"
                                    coin={coin}
                                    clusters={snapshot.longs.slice(0, 2)}
                                    side="long"
                                />
                                <ClusterList
                                    title="Above — shorts get squeezed"
                                    tone="short"
                                    coin={coin}
                                    clusters={snapshot.shorts.slice(0, 2)}
                                    side="short"
                                />

                                {verdict && (
                                    <p
                                        className={cn(
                                            'rounded-md border px-2 py-1.5 font-medium',
                                            verdict.tone === 'danger' &&
                                                'border-destructive/40 bg-destructive/10 text-destructive',
                                            verdict.tone === 'warn' &&
                                                'border-amber-400/40 bg-amber-400/10 text-amber-200',
                                            verdict.tone === 'ok' &&
                                                'border-primary/30 bg-primary/10 text-primary',
                                        )}
                                        role="status"
                                    >
                                        {verdict.text}
                                    </p>
                                )}
                                {!trade && (
                                    <p className="text-muted-foreground">
                                        Open a position and this card checks
                                        your stop-loss against the bands.
                                    </p>
                                )}

                                <p
                                    className={cn(
                                        'text-[10px] text-muted-foreground',
                                        snapshot.stale && 'text-amber-300',
                                    )}
                                >
                                    {snapshot.stale ? 'Map is stale — ' : ''}
                                    {explainSource(snapshot)}
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    },
);

const ClusterList: React.FC<{
    title: string;
    tone: 'long' | 'short';
    coin: string;
    clusters: LiqMapSnapshot['longs'];
    side: 'long' | 'short';
}> = ({ title, tone, coin, clusters, side }) => {
    if (clusters.length === 0) return null;
    return (
        <div className="flex flex-col gap-1">
            <div
                className={cn(
                    'text-[9px] font-semibold uppercase tracking-[0.08em]',
                    tone === 'long' ? 'text-primary/80' : 'text-destructive/80',
                )}
            >
                {title}
            </div>
            {clusters.map((c) => (
                <div
                    key={`${side}-${c.price}`}
                    className="flex flex-col"
                    title={explainCluster(coin, c, side)}
                >
                    <div className="flex items-baseline justify-between gap-2 font-mono tabular-nums">
                        <span className="text-foreground">
                            ${formatLevelPrice(c.price)}
                        </span>
                        <span className="text-muted-foreground">
                            {c.distancePct > 0 ? '+' : ''}
                            {c.distancePct.toFixed(1)}%
                        </span>
                        <span
                            className={cn(
                                'font-bold',
                                tone === 'long'
                                    ? 'text-primary'
                                    : 'text-destructive',
                            )}
                        >
                            {formatNotional(c.notionalUsd)}
                        </span>
                        <span className="text-muted-foreground">
                            {c.wallets} wallets
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default LiqMapLegend;
